import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from '../types';

import { MediaSearchTool } from 'js/src/agent/tools/MediaSearchTool';

export const findStockMediaSkill: Skill = {
    name: SKILL_NAMES.findStockMedia,
    description: 'How to search stock media. Load BEFORE your first stock media search.',
    kind: 'howto',
    surfaces: ['sketch'],
    get instructions(): string {
        return `# Finding Great Stock Media

How to search the stock library (\`${MediaSearchTool.toolName}\`, \`source: "library"\`) so the results
look like a high-end brand ad, not generic stock. Only reach for stock after the
user's own material is exhausted — their media beats stock.

## Search craft
- Run at least 2 searches for every ~5 seconds of video — a 60-second video means
  24+ stock searches, not a handful. Come at each beat from different angles;
  searches are cheap and run in parallel, so batch them. Search for the **literal thing, not the concept**,
  such as "barista pulling espresso shot", not "productivity".
- Use cinematic descriptors to surface premium-looking results: drone shot,
  close-up, bokeh, slow motion, golden hour.
- Default to \`media_type: "videos"\` — a video built on stills looks like a
  slideshow. A stock photo is a true last resort, allowed only when repeated video
  searches from different angles turn up nothing relevant, never because a photo
  turned up first. About to use a stock photo? That's the trigger to search video
  again with different terms.

## The quality bar
**Professional, cinematic footage of the real world** — authentic scenes, natural
light, real subjects, clean camera work. Avoid cheesy, clichéd stock — it's the #1
media problem, and "relevant + high-res" does not make a clip acceptable. The
offenders are on-topic and technically fine, which is exactly why they slip
through, so screen deliberately:
- **No staged corporate cheese**: fake smiling teams around a laptop, posed
  handshakes, thumbs-ups, high-fives, cheering at a whiteboard, a headset agent
  grinning at the camera.
- **No literal metaphor clips**: lightbulb for "idea", turning gears for "process",
  a rocket or upward arrow for "growth", a ladder or summit for "success", glowing
  brain, puzzle pieces, a maze, a compass, dominoes.
- **No dated or over-processed look**: oversaturated 2000s stock, cheap 3D renders,
  watermarked or low-res footage.
- **No "tech" HUD / data-visualization footage — be especially strict.** Glowing
  hologram interfaces, floating UI panels, animated data grids, glowing network
  lines, digital globes, streaming code, particle swarms, light streaks, lens
  flares, sparkle overlays: always out. For an abstract or "techy" topic, pick real
  footage of the actual context (a real person at a real laptop, a real office, a
  real product).
- Instead, go concrete and authentic: real footage of the actual subject, real
  people doing the real activity, textured environments, honest documentary-style
  shots. For an abstract concept, prefer a concrete real-world scene tied to it
  over a visual metaphor.
- Gut check before using any clip: would this look at home in a polished brand's
  video, or does it scream generic stock? If the latter, search again — don't
  settle.`;
    },
};
